import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, Button, Chip, CircularProgress, Container, Paper, Stack, Typography} from '@mui/material';
import ApiService from '../network/API';

const errText = (e, fallback) => (typeof e === 'string' ? e : e?.reason || e?.message) || fallback;

// Вкладка «Экзамены»: все экзамены по всем предметам в одном месте. Билеты и создание экзамена — на странице предмета.
const AdminExamsPage = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState(null);
    const [tags, setTags] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        ApiService.getAllExamEvents().then(setEvents).catch((e) => setError(errText(e, 'Не удалось загрузить экзамены')));
        ApiService.getAllTags().then(setTags).catch(() => {});
    }, []);

    const bySubject = useMemo(() => {
        const map = new Map();
        (events || []).forEach((ev) => {
            if (!map.has(ev.tagId)) map.set(ev.tagId, {tagId: ev.tagId, tagName: ev.tagName, events: []});
            map.get(ev.tagId).events.push(ev);
        });
        return [...map.values()];
    }, [events]);

    const withoutEvents = useMemo(() => {
        const used = new Set(bySubject.map((s) => s.tagId));
        return tags.filter((t) => !used.has(t.id));
    }, [tags, bySubject]);

    if (!events && !error) return <Box display="flex" justifyContent="center" py={8}><CircularProgress/></Box>;

    return (
        <Container maxWidth="md" sx={{py: 4}}>
            <Typography variant="h5" fontWeight={800} gutterBottom>Экзамены</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Экзамен проводится для группы по предмету. Билеты и новый экзамен — на странице предмета.
            </Typography>
            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {bySubject.length === 0 && !error && (
                <Typography color="text.disabled" sx={{mb: 2}}>Пока нет ни одного экзамена. Выберите предмет ниже и создайте.</Typography>
            )}

            <Stack spacing={2}>
                {bySubject.map((s) => (
                    <Paper key={s.tagId} variant="outlined" sx={{p: 2, borderRadius: 2}}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1}}>
                            <Typography fontWeight={700} sx={{flex: 1}}>{s.tagName}</Typography>
                            <Button size="small" onClick={() => navigate(`/admin/exams/${s.tagId}`)}>Билеты и новый экзамен</Button>
                        </Stack>
                        <Stack spacing={0.75}>
                            {s.events.map((ev) => (
                                <Paper key={ev.id} variant="outlined" sx={{p: 1.25, borderRadius: 1.5, cursor: 'pointer'}}
                                       onClick={() => navigate(`/admin/exam-events/${ev.id}`)}>
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Typography sx={{flex: 1, minWidth: 160}} fontWeight={600}>{ev.title}</Typography>
                                        <Chip size="small" label={ev.groupName}/>
                                        <Chip size="small" variant="outlined" label={`${ev.durationMinutes} мин`}/>
                                        <Chip size="small" variant="outlined" color={ev.started > 0 ? 'primary' : 'default'}
                                              label={`начали ${ev.started} из ${ev.students}`}/>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            {withoutEvents.length > 0 && (
                <Box sx={{mt: 3}}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>Предметы без экзаменов</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {withoutEvents.map((t) => (
                            <Chip key={t.id} label={t.name} onClick={() => navigate(`/admin/exams/${t.id}`)} variant="outlined"/>
                        ))}
                    </Stack>
                </Box>
            )}
        </Container>
    );
};

export default AdminExamsPage;
